import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Ellipsis, Plus } from 'lucide-react';
import clsx from 'clsx';
import { PROVIDERS, type ProviderDef, type ProviderId } from '@sequesto-os/shared';
import { useTriggerSync, useTestIntegration } from '../../hooks/useIntegrations';
import {
  HEALTH_DOT,
  HEALTH_LABEL,
  integrationDisplayName,
  integrationStatus,
  type DerivedHealth,
} from '../../components/SystemDiagram';
import { Spinner } from '../../components/ui/Spinner';
import { formatDayMonthTime, formatDayMonthYearTime } from '../../utils/dateFormat';
import { RowTags } from './RowTags';
import { ErrorCause } from './ErrorCause';
import type { Integration } from '../../api/integrations';

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_7rem_7.5rem_2rem] items-start gap-4 px-4 py-2.5';

/** lastActivity as the API sends it: `by` names what used it (an automation, Sales research…). */
type Activity = DerivedHealth['lastActivity'];

/**
 * The "Last activity" cell always says what the time is: "Synced 3h ago" for
 * tools the OS syncs, "Used 5m ago" for key-only tools (Slack, OpenAI…) whose
 * last recorded use is known. With no use on record it says why: "No use
 * seen" when the OS records this tool's use, "Not recorded" when the OS calls
 * it without keeping a trace, "Not called by the OS" for keys kept for people.
 * `failingFrom`: when another automation is failing, the cell names the one
 * that used the tool, so "Used 5m ago" never reads as the failing job's run.
 */
function lastActivityCell(
  h: Pick<DerivedHealth, 'hasSync' | 'usageTracked' | 'notCalledByOs'> & { lastActivity: Activity },
  failingFrom?: string | null,
): { text: ReactNode; title?: string } {
  const at = h.lastActivity?.at;
  const by = h.lastActivity?.by ?? null;
  if (!h.hasSync) {
    if (!at) {
      if (h.notCalledByOs) {
        return { text: <span className="text-text-tertiary">Not called by the OS</span>, title: 'Only the key is stored: people or an MCP use it, no OS code calls it' };
      }
      return h.usageTracked
        ? { text: <span className="text-text-tertiary">No use seen</span>, title: 'No recorded use in the last 90 days' }
        : { text: <span className="text-text-tertiary">Not recorded</span>, title: 'The OS calls this tool but keeps no record of when' };
    }
    const named = by && failingFrom && by !== failingFrom;
    return {
      text: named ? (
        <>
          Used {formatRelativeTime(at)}
          <span className="block break-words text-text-tertiary">by {by}</span>
        </>
      ) : (
        `Used ${formatRelativeTime(at)}`
      ),
      title: `Last successful use${by ? ` by ${by}` : ''} · ${formatDayMonthYearTime(at)}`,
    };
  }
  return at
    ? { text: `Synced ${formatRelativeTime(at)}`, title: `Last finished sync · ${formatDayMonthYearTime(at)}` }
    : { text: 'Never synced' };
}

/** "Synced 15h ago" for a tool the OS syncs with a sync on record, else null. */
export function syncedText(h: Pick<DerivedHealth, 'hasSync'> & { lastActivity: Activity }): string | null {
  return h.hasSync && h.lastActivity?.at ? `Synced ${formatRelativeTime(h.lastActivity.at)}` : null;
}

/**
 * The sync time most rows of a list share ("15h ago"), so it is said once in
 * the list's heading and each row shows its time only where it differs.
 * Null unless at least 4 rows share it and they are most of the synced rows.
 */
export function sharedSyncAge(texts: readonly (string | null)[]): string | null {
  const present = texts.filter((t): t is string => t !== null);
  const tally = new Map<string, number>();
  for (const t of present) tally.set(t, (tally.get(t) ?? 0) + 1);
  const [top, n] = [...tally.entries()].sort((a, b) => b[1] - a[1])[0] ?? [null, 0];
  return top && n >= 4 && n >= present.length * 0.6 ? top : null;
}

/** "Last use failed: Out of credit (402) · Sales research · 4 Sep": a fact from the past, muted. */
function StaleFailureNote({ health }: { health: DerivedHealth | undefined }) {
  if (!health || health.state !== 'key_set' || !health.reason) return null;
  const parts = [health.reason, health.source, health.since ? formatDayMonthTime(health.since) : null].filter(Boolean);
  return <p className="mt-0.5 break-words text-body-xs text-text-tertiary">{parts.join(' · ')}</p>;
}

/** `showStatus` false under a heading that already names every row's status ("Connected"). */
export function IntegrationListHead({ showStatus = true, showActivity = true }: { showStatus?: boolean; showActivity?: boolean }) {
  return (
    <div className={clsx(ROW_GRID, 'border-b border-border-light py-2 text-body-xs font-medium text-text-tertiary')}>
      <span>Integration</span>
      <span>{showStatus ? 'Status' : ''}</span>
      <span>{showActivity ? 'Last activity' : ''}</span>
      <span />
    </div>
  );
}

/**
 * One connected integration on one line: name (what the OS uses it for is the
 * name's tooltip), team tags, a status only when it isn't healthy, last sync,
 * and Sync / Test / Configure behind a ⋯ menu.
 */
export function IntegrationRow({
  integration,
  onConfigure,
  isAdmin,
  showActivity = true,
  sharedSync = null,
}: {
  /** False under a heading where no row has a use on record. */
  showActivity?: boolean;
  /** The "Synced 15h ago" text the list's heading already states for most rows. */
  sharedSync?: string | null;
  integration: Integration;
  onConfigure: () => void;
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const triggerSync = useTriggerSync();
  const testConnection = useTestIntegration();
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const status = integrationStatus(integration);
  // Only a live run counts; a stale "running" row is Stuck and must not poll forever.
  const [isSyncing, setIsSyncing] = useState(() => status.state === 'syncing');
  const lastSyncAtRef = useRef(integration.lastSyncAt);

  // Pick up syncs started elsewhere (e.g. an automation's Run now).
  useEffect(() => {
    if (status.state === 'syncing' && !isSyncing) {
      lastSyncAtRef.current = integration.lastSyncAt;
      setIsSyncing(true);
    }
  }, [status.state]);

  // Poll while a sync runs so "Last activity" updates; give up after 10 minutes.
  useEffect(() => {
    if (!isSyncing) return;
    const interval = setInterval(() => queryClient.invalidateQueries({ queryKey: ['integrations'] }), 5000);
    const timeout = setTimeout(() => setIsSyncing(false), 600_000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isSyncing, queryClient]);

  useEffect(() => {
    if (isSyncing && status.state !== 'syncing' && !triggerSync.isPending) {
      setIsSyncing(false);
      lastSyncAtRef.current = integration.lastSyncAt;
    }
  }, [isSyncing, status.state, integration.lastSyncAt, triggerSync.isPending]);

  const handleSync = () => {
    setFeedback(null);
    lastSyncAtRef.current = integration.lastSyncAt;
    triggerSync.mutate(
      { id: integration.id },
      {
        onSuccess: () => {
          setFeedback({ type: 'success', message: 'Sync started' });
          setIsSyncing(true);
          setTimeout(() => setFeedback(null), 4000);
        },
        onError: (error) =>
          setFeedback({ type: 'error', message: error instanceof Error ? error.message : 'Sync failed to start' }),
      }
    );
  };

  const handleTest = () => {
    setFeedback(null);
    testConnection.mutate(integration.id, {
      onSuccess: ({ result }) => setFeedback({ type: result.success ? 'success' : 'error', message: result.message }),
      onError: (error) =>
        setFeedback({ type: 'error', message: error instanceof Error ? error.message : 'Test failed' }),
    });
  };

  const def: ProviderDef | undefined = PROVIDERS[integration.provider as ProviderId];
  const providerName = def?.name ?? integration.provider;
  const health = status.state;
  const name = integrationDisplayName(integration);
  const busy = triggerSync.isPending || testConnection.isPending;
  const problem = health === 'failing' || health === 'stuck';
  const last = lastActivityCell(status, problem ? status.source : null);

  return (
    <div id={`integration-${integration.id}`} className={clsx(ROW_GRID, 'scroll-mt-20', health === 'failing' && 'bg-error-light/30')}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={isAdmin ? onConfigure : undefined}
            title={def?.usage ?? def?.description}
            className={clsx('text-body-sm font-medium text-text-primary', isAdmin && 'hover:underline')}
          >
            {name}
          </button>
          {/* The provider only when it adds something ("Fathom (Walter)" → Fathom), never "Clay / Clay" */}
          <RowTags
            labels={[providerName.toLowerCase() !== name.toLowerCase() ? providerName : null]}
            tags={def?.tags}
          />
        </div>
        {/* The derived reason, not the row's last error: a stale error on a
            now-healthy row would contradict the status beside it. */}
        {problem && (
          <ErrorCause
            className={clsx('mt-1', health === 'failing' ? 'text-error-dark' : 'text-warning-dark')}
            cause={status.reason ?? (health === 'stuck' ? 'Sync started but never finished' : 'Sync failed')}
            // The raw error behind "Show details": the automation's own reason
            // when it came from one, else the row's last sync error.
            raw={status.source ? status.reason : (integration.lastSyncError ?? status.reason)}
            suffix={[status.source ? `from ${status.source}` : null, status.since ? `since ${formatDayMonthTime(status.since)}` : null]
              .filter(Boolean)
              .map((p) => ` · ${p}`)
              .join('')}
          />
        )}
        <StaleFailureNote health={status} />
        {feedback && (
          <p className={clsx('mt-1 text-body-xs', feedback.type === 'success' ? 'text-success-dark' : 'text-error-dark')}>
            {feedback.message}
          </p>
        )}
      </div>

      {/* Every healthy row says what it is: "Connected" once the OS has seen it
          work (a sync or a recorded use), "Key set" for a key with no use seen. */}
      {/* Connected and Key set rows sit under a heading that says so: no
          column repeating one word. Only the "Needs a look" rows carry a status. */}
      {health === 'key_set' || health === 'ok' ? (
        <span />
      ) : (
        <span className="flex items-center gap-1.5 pt-0.5 text-body-xs text-text-secondary">
          <span className={clsx('h-2 w-2 flex-none rounded-full', HEALTH_DOT[health], health === 'syncing' && 'animate-pulse')} />
          {HEALTH_LABEL[health]}
        </span>
      )}

      {showActivity ? (
        <span className="pt-0.5 text-body-xs tabular-nums text-text-secondary" title={last.title}>
          {sharedSync && last.text === sharedSync ? null : last.text}
        </span>
      ) : (
        <span />
      )}

      <div className="flex justify-end">
        {isAdmin &&
          (busy ? (
            <Spinner size="sm" className="mt-1 text-text-tertiary" />
          ) : (
            <Menu as="div" className="relative">
              <MenuButton
                aria-label={`Actions for ${name}`}
                className="flex h-7 w-7 items-center justify-center rounded-full text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary"
              >
                <Ellipsis className="h-4 w-4" />
              </MenuButton>
              <MenuItems
                anchor="bottom end"
                className="z-50 w-40 rounded-lg border border-border-light bg-surface-primary py-1 shadow-medium [--anchor-gap:4px] focus:outline-none"
              >
                <MenuItem disabled={integration.status !== 'active' || !status.hasSync}>
                  <button
                    type="button"
                    onClick={handleSync}
                    className="block w-full px-3 py-1.5 text-left text-body-sm text-text-secondary data-[disabled]:opacity-40 data-[focus]:bg-surface-secondary"
                  >
                    Sync now
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    type="button"
                    onClick={handleTest}
                    className="block w-full px-3 py-1.5 text-left text-body-sm text-text-secondary data-[focus]:bg-surface-secondary"
                  >
                    Test connection
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    type="button"
                    onClick={onConfigure}
                    className="block w-full px-3 py-1.5 text-left text-body-sm text-text-secondary data-[focus]:bg-surface-secondary"
                  >
                    Configure
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          ))}
      </div>
    </div>
  );
}

/** A provider that is not connected yet (admins can add it). */
export function AvailableRow({
  def,
  onAdd,
  keySource,
  health,
  note,
  noteSource,
  showActivity = true,
}: {
  def: { id?: string; name: string; description: string; category: string; usage?: string; tags?: ProviderDef['tags'] };
  onAdd?: () => void;
  /** Set when a key exists without a connection row: listed with the connected ones as "Key set". */
  keySource?: 'row' | 'secret' | 'env' | null;
  /** The derived status of a key-only row, for its last use. */
  health?: DerivedHealth;
  /** Why a not-connected tool needs a look ("… runs but skips"). */
  note?: string;
  /** The automation behind `note`. */
  noteSource?: string | null;
  /** False under a heading where no row has a use on record: no column of identical "Not recorded". */
  showActivity?: boolean;
}) {
  return (
    <div id={def.id ? `integration-${def.id}` : undefined} className={clsx(ROW_GRID, 'scroll-mt-20')}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          {/* A key-only row reads like the connected rows around it: name and team tags only */}
          <span className={clsx('text-body-sm font-medium', keySource ? 'text-text-primary' : 'text-text-secondary')}>{def.name}</span>
          {/* Team tags only, the one label set every row uses (never "Advertising" or "AI") */}
          <RowTags tags={def.tags} />
        </div>
        {keySource ? (
          <StaleFailureNote health={health} />
        ) : (
          <p className="mt-0.5 text-body-xs text-text-tertiary">{def.usage ?? def.description}</p>
        )}
      </div>
      {/* "Not connected" is the section heading; not repeated on every row */}
      {keySource ? (
        // Under "Connected" or "Key set, no use seen": the heading is the status.
        <span className="pt-0.5 text-body-xs text-text-tertiary" title={keySource === 'env' ? 'Key found in the API environment; no connection row' : 'Key found in the secret store; no connection row'}>
          {keySource === 'env' ? 'Key in env' : 'Key in secrets'}
        </span>
      ) : note ? (
        // An automation that runs but skips here is a fact, not an exception: muted, in the status column.
        <span className="pt-0.5 text-body-xs text-text-secondary" title={noteSource ? `${noteSource}: ${note}` : note}>
          Its job skips
        </span>
      ) : (
        <span />
      )}
      {keySource && showActivity ? (
        (() => {
          const last = lastActivityCell({
            lastActivity: health?.lastActivity ?? null,
            hasSync: false,
            usageTracked: !!health?.usageTracked,
            notCalledByOs: health?.notCalledByOs,
          });
          return (
            <span className="pt-0.5 text-body-xs tabular-nums text-text-secondary" title={last.title}>
              {last.text}
            </span>
          );
        })()
      ) : (
        <span />
      )}
      <div className="flex justify-end">
        {/* A key-only row sits among the connected ones, so it gets their "…" menu, not the "+" of a new tool */}
        {onAdd && keySource ? (
          <Menu as="div" className="relative">
            <MenuButton
              aria-label={`Actions for ${def.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-full text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary"
            >
              <Ellipsis className="h-4 w-4" />
            </MenuButton>
            <MenuItems
              anchor="bottom end"
              className="z-50 w-40 rounded-lg border border-border-light bg-surface-primary py-1 shadow-medium [--anchor-gap:4px] focus:outline-none"
            >
              <MenuItem>
                <button
                  type="button"
                  onClick={onAdd}
                  className="block w-full px-3 py-1.5 text-left text-body-sm text-text-secondary data-[focus]:bg-surface-secondary"
                >
                  Configure
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        ) : onAdd && (
          <button
            type="button"
            onClick={onAdd}
            title={`Add ${def.name}`}
            aria-label={`Add ${def.name}`}
            className="flex h-7 w-7 items-center justify-center rounded-full text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
